import {
  Megaphone,
  Sparkles,
  Video,
  Radio,
  Rocket,
  Bell,
  Zap,
  Calendar,
  PlayCircle,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AnnouncementIcon, useAnnouncementBanner } from '@/hooks/useAnnouncementBanner';

const ICON_MAP: Record<AnnouncementIcon, LucideIcon> = {
  Megaphone,
  Sparkles,
  Video,
  Radio,
  Rocket,
  Bell,
  Zap,
  Calendar,
  PlayCircle,
};

interface Props {
  className?: string;
}

export function AnnouncementPopover({ className }: Props) {
  const { data } = useAnnouncementBanner();
  if (!data?.enabled || !data.text) return null;

  const Icon = ICON_MAP[data.icon] ?? Megaphone;
  const hasCta = !!(data.cta_label && data.cta_url);
  const isExternal = hasCta && /^https?:\/\//i.test(data.cta_url);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label="Ver anuncio"
          className={cn(
            'relative inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors',
            className,
          )}
        >
          <Icon className="h-5 w-5" />
          <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground break-words">{data.text}</p>
            {hasCta && (
              <a
                href={data.cta_url}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                {data.cta_label}
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AnnouncementPopover;
